import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Sheep, SheepParent } from '../types';

interface Props {
  sheep: Sheep;
  readOnly?: boolean; // true = public page, use href instead of navigate
}

const MALE_STYLE = { border: '2px solid #1677ff', background: '#e6f4ff' };
const FEMALE_STYLE = { border: '2px solid #eb2f96', background: '#fff0f6' };
const CENTER_STYLE = { border: '2px solid #764ba2', background: '#f9f0ff' };

function genderIcon(g: string) { return g === 'male' ? '♂' : '♀'; }
function cardStyle(g: string) { return g === 'male' ? MALE_STYLE : FEMALE_STYLE; }

interface NodeCardProps {
  id: number;
  name: string;
  tagNumber: string;
  gender: string;
  birthDate?: string;
  breed?: string;
  style?: React.CSSProperties;
  center?: boolean;
  readOnly?: boolean;
}

function NodeCard({ id, name, tagNumber, gender, birthDate, breed, style, center, readOnly }: NodeCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (center) return;
    if (readOnly) {
      window.location.href = `/sheep/${id}`;
    } else {
      navigate(`/sheep/${id}/detail`);
    }
  };

  return (
    <Card
      size="small"
      onClick={center ? undefined : handleClick}
      style={{
        minWidth: center ? 180 : 140,
        borderRadius: 10,
        cursor: center ? 'default' : 'pointer',
        transition: 'box-shadow 0.2s',
        ...(center ? CENTER_STYLE : cardStyle(gender)),
        ...style,
      }}
      styles={{ body: { padding: '8px 12px' } }}
      onMouseEnter={(e) => { if (!center) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'; }}
      onMouseLeave={(e) => { if (!center) e.currentTarget.style.boxShadow = ''; }}
    >
      <div style={{ fontWeight: center ? 700 : 600, fontSize: center ? 15 : 13 }}>
        {genderIcon(gender)} {name}
      </div>
      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{tagNumber}</div>
      {breed && <Tag style={{ marginTop: 3, fontSize: 10 }}>{breed}</Tag>}
      {birthDate && (
        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
          {new Date(birthDate).getFullYear()}
        </div>
      )}
    </Card>
  );
}

// Connector lines via SVG overlay — simple CSS approach
function VLine({ height = 24 }: { height?: number }) {
  return <div style={{ width: 2, height, background: '#d9d9d9', margin: '0 auto' }} />;
}

function HLine() {
  return <div style={{ height: 2, background: '#d9d9d9', flex: 1, alignSelf: 'center' }} />;
}

export default function PedigreeTree({ sheep, readOnly = false }: Props) {
  const children: SheepParent[] = [
    ...(sheep.childrenAsFather || []),
    ...(sheep.childrenAsMother || []),
  ];

  const hasParents = !!(sheep.father || sheep.mother);
  const hasChildren = children.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, padding: '8px 16px' }}>

      {/* ── Parents row ── */}
      {hasParents && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0, width: '100%', justifyContent: 'center' }}>
            {sheep.father && sheep.mother ? (
              <>
                {/* Father */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: 200 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>Отец</Typography.Text>
                  <NodeCard {...sheep.father} readOnly={readOnly} />
                  <VLine />
                </div>
                {/* gap connector */}
                <div style={{ width: 40, height: 2, background: '#d9d9d9', alignSelf: 'flex-end', marginBottom: 24 }} />
                {/* Mother */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: 200 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>Мать</Typography.Text>
                  <NodeCard {...sheep.mother} readOnly={readOnly} />
                  <VLine />
                </div>
              </>
            ) : sheep.father ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>Отец</Typography.Text>
                <NodeCard {...sheep.father} readOnly={readOnly} />
                <VLine />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, marginBottom: 4 }}>Мать</Typography.Text>
                <NodeCard {...sheep.mother!} readOnly={readOnly} />
                <VLine />
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Current sheep ── */}
      <NodeCard
        id={sheep.id}
        name={sheep.name}
        tagNumber={sheep.tagNumber}
        gender={sheep.gender}
        birthDate={sheep.birthDate}
        breed={sheep.breed}
        center
        readOnly={readOnly}
      />

      {/* ── Children row ── */}
      {hasChildren && (
        <>
          <VLine />
          {children.length === 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <NodeCard {...children[0]} readOnly={readOnly} />
              <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                {children[0].gender === 'male' ? 'Сын' : 'Дочь'}
              </Typography.Text>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', justifyContent: 'center', width: '100%' }}>
              {children.map((child, i) => (
                <React.Fragment key={child.id}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: 180 }}>
                    <NodeCard {...child} readOnly={readOnly} />
                    <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                      {child.gender === 'male' ? 'Сын' : 'Дочь'}
                    </Typography.Text>
                  </div>
                  {i < children.length - 1 && (
                    <div style={{ width: 16, height: 2, background: '#d9d9d9', alignSelf: 'flex-start', marginTop: 20 }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </>
      )}

      {!hasParents && !hasChildren && (
        <Typography.Text type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
          Родственники не указаны
        </Typography.Text>
      )}
    </div>
  );
}
